import axios from '../../axiosConfig';
import React, { useState } from 'react';
import './Signup.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleLogin } from '@react-oauth/google';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post('/auth/google', {
        credential: credentialResponse.credential,
      });

      if (response?.data?.success) {
        const { token, userRole } = response.data;
        localStorage.setItem('jwtToken', token);
        localStorage.setItem('userRole', userRole);
        toast.success('Google signup successful!');
        navigate('/');
      } else {
        toast.error(response?.data?.message || 'Google sign-up failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Google sign-up failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google sign-up could not be completed. Add your app URL to the Authorized JavaScript origins in Google Cloud Console, such as http://localhost:3000 and https://www.ambitionjet.com.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim() || !formData.confirmPassword.trim()) {
      toast.error('All fields are required!');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not available. Try again later.');
      return;
    }

    setIsSubmitting(true);
    const token = await executeRecaptcha('signup');

    try {
      const response = await axios.post('/signup', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        captchaToken: token,
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response?.data?.success) {
        toast.success('Signup successful! You can now log in.');
        navigate('/login');
        setFormData({ name: '', email: '', password: '', confirmPassword: '' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Create your account</h2>
          <p>Join the community to publish and manage your own blogs.</p>
        </div>

        <div className="auth-social">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            text="signup_with"
            shape="rectangular"
            width="100%"
          />
        </div>

        <div className="auth-divider">
          <span>or create an account with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label>Full name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Jane Doe" required />
          </div>

          <div className="form-field">
            <label>Email address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required />
          </div>

          <div className="form-field">
            <label>Password</label>
            <div className="password-field">
              <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder="At least 8 characters" required />
              <button type="button" className="password-toggle" onClick={() => setShowPassword((prev) => !prev)}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="form-field">
            <label>Confirm password</label>
            <div className="password-field">
              <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="Re-enter password" required />
              <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword((prev) => !prev)}>
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
